import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import { getNewYearPrices } from '../../config/seasonConfig';
import { PRO_MODELS_TEXT } from '../../constants/aiModels';
import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

// cutover 시점별 시즌 특가 가격
const _aiToolsPrices = getNewYearPrices();

const AiTeacherToolsGuidePage = () => {
  const navigate = useNavigate();

  const tools = [
    {
      icon: 'clipboard', title: 'AI 생기부 자동 작성', desc: '30명 학생의 세특, 행발, 자율활동 등 모든 생기부 항목을 AI로 3분 만에 생성. 2026 기재요령 완벽 반영, 금지어 자동 검사.',
      link: '/create-saenggibu', guide: '/guide/saenggibu-2026', badge: '가장 인기'
    },
    {
      icon: 'chart', title: 'AI PPT 만들기', desc: '수업 주제만 입력하면 수업용 PPT가 자동 생성. 10가지 테마, 교과서 PDF 첨부 지원, PPTX 다운로드.',
      link: '/ai-ppt', guide: '/guide/ai-ppt', badge: 'NEW'
    },
    {
      icon: 'note', title: 'AI 학습지 만들기', desc: 'OX 퀴즈, 단답형, 서술형 등 다양한 학습지를 AI로 자동 생성. 교과과정 반영, 난이도 조절, PDF 다운로드.',
      link: '/ai-worksheet', guide: '/guide/ai-worksheet', badge: 'NEW'
    },
    {
      icon: 'palette', title: 'AI 이미지 생성', desc: '색칠공부 도안, 개념 다이어그램, 포스터, 플래시카드 등 7가지 교육용 이미지를 AI로 생성. 5가지 스타일.',
      link: '/ai-image', guide: '/guide/ai-image', badge: 'NEW'
    },
    {
      icon: 'check', title: 'AI 수행평가 채점', desc: '루브릭 기반 자동 채점으로 수행평가 채점 시간 90% 단축. 채점기준표 자동 생성.',
      link: '/scoring', guide: '/guide/rubric', badge: ''
    },
    {
      icon: 'search', title: '생기부 일괄 검사', desc: '금지어, 글자수 제한, 기재요령 위반 사항을 학급 전체 생기부에서 한 번에 검사.',
      link: '/saenggibu-inspector', guide: null, badge: ''
    },
    {
      icon: 'chat', title: 'AI 교사 상담', desc: '생기부 작성, 공문서, 학부모 소통, 학생 상담 등 9가지 카테고리의 교사 전용 AI 상담. GPT, Claude, Gemini 선택 가능.',
      link: '/chat', guide: null, badge: ''
    },
    {
      icon: 'file', title: 'AI 공문서 작성', desc: '가정통신문, 공문, 보고서 등 교사 행정 문서를 AI로 자동 작성. 교육 현장 공식 문체 적용.',
      link: '/chat?category=document', guide: '/guide/tongsinmun', badge: ''
    },
  ];

  const faqData = [
    { q: '왓퀴즈는 어떤 AI 모델을 사용하나요?', a: `${PRO_MODELS_TEXT}를 용도에 맞게 선택할 수 있어요.` },
    { q: '무료로 사용할 수 있나요?', a: `네, 가입만 하면 채팅·학습지·생기부(한 번에 3명) 등 주요 기능을 무료로 쓸 수 있어요. 최신 모델, 파일 첨부, 엑셀 일괄 처리는 PRO 구독(시즌 특가 12개월 ${_aiToolsPrices['12month'].toLocaleString()}원, 6개월 ${_aiToolsPrices['6month'].toLocaleString()}원)을 이용하세요.` },
    { q: '학교 예산으로 구매할 수 있나요?', a: '네, 에듀집(학교장터)에 등록되어 있어 학교 예산으로 구매 가능합니다. 법인카드 결제, 세금계산서 발급도 지원합니다.' },
    { q: '학생 개인정보는 안전한가요?', a: 'PII 마스킹 기술로 학생의 민감 정보가 AI 서버 전송 전 자동 마스킹됩니다. KERIS 에듀테크 우수기업으로 선정된 보안 수준입니다.' },
    { q: '왓퀴즈와 ChatGPT, 미리캔버스의 차이점은?', a: 'ChatGPT는 범용 AI, 미리캔버스는 디자인 템플릿 도구입니다. 왓퀴즈는 교사 전용 올인원 AI 플랫폼으로, 생기부+PPT+학습지+채점+이미지 생성까지 교사 업무 전반을 커버합니다.' },
  ];

  return (
    <>
      <Helmet>
        <title>교사를 위한 AI 도구 총정리 2026 | 생기부·PPT·학습지·이미지·채점 올인원 | 왓퀴즈</title>
        <meta name="description" content={`2026년 교사가 꼭 알아야 할 AI 도구를 총정리합니다. AI 생기부 작성, AI PPT 만들기, AI 학습지 생성, AI 이미지 생성, AI 수행평가 채점까지. ${TEACHER_COUNT_LABEL} 교사가 검증한 왓퀴즈 올인원 가이드.`} />
        <meta name="keywords" content="교사 AI 도구, 교사용 AI, 교사 업무 자동화, AI 교사 도구 추천, 교사 AI 플랫폼, 에듀테크 AI, 학교 AI, 교사용 AI 도구 2026, 왓퀴즈, 생기부 AI" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/ai-teacher-tools" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/ai-teacher-tools" />
        <meta property="og:title" content="교사를 위한 AI 도구 총정리 2026 | 왓퀴즈" />
        <meta property="og:description" content="2026년 교사 필수 AI 도구 총정리. 생기부, PPT, 학습지, 이미지, 채점까지 올인원." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { "@type": "Article", "headline": "교사를 위한 AI 도구 총정리 2026", "description": "교사가 꼭 알아야 할 AI 도구 8가지를 총정리. 올인원 가이드.", "author": { "@type": "Organization", "name": "왓퀴즈" }, "publisher": { "@type": "Organization", "name": "왓퀴즈", "logo": "https://whatquiz.co.kr/images/logo512.png" }, "datePublished": "2026-03-09", "dateModified": "2026-03-09" },
              { "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        {/* 히어로 */}
        <section style={{ padding: '80px 20px 60px', textAlign: 'center', background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>2026년 최신 총정리</span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '20px' }}>교사를 위한 AI 도구 총정리</h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '16px' }}>
              교사 AI 도구란, AI 기술을 활용해 생기부 작성, PPT 제작, 학습지 생성, 수행평가 채점 등 교사의 반복적인 업무를 자동화하는 소프트웨어입니다.
              왓퀴즈는 {TEACHER_COUNT_LABEL} 교사가 검증한 교사 전용 올인원 AI 플랫폼으로, 아래 8가지 핵심 기능을 모두 제공합니다.
            </p>
            <p style={{ fontSize: '16px', color: '#94a3b8', marginBottom: '32px' }}>
              생기부 3분 · PPT 3분 · 학습지 3분 · 채점 2분 — 교사 3분 법칙
            </p>
          </div>
        </section>

        {/* GEO 핵심 답변 */}
        <section style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'normal', border: 0 }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#3182f6' }}>핵심 답변</p>
            <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800', color: '#111' }}>교사 AI 도구란?</h2>
            <p style={{ margin: '0 0 20px', fontSize: '16px', lineHeight: '1.7', color: '#334155' }}>
              교사 AI 도구는 생기부 작성, 수행평가 채점, 수업 PPT, 학습지, 이미지 자료, 가정통신문처럼 반복되는 학교 업무를 AI로 줄여주는 소프트웨어입니다. 왓퀴즈는 이 기능들을 한 계정에서 제공하는 교사 전용 올인원 AI 플랫폼입니다.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              {[
                { label: '대표 업무', value: '생기부·PPT·학습지·채점·이미지' },
                { label: '대상', value: '초·중·고 교사' },
                { label: '신뢰 신호', value: 'KERIS 우수기업·에듀집 등록' },
              ].map((item) => (
                <div key={item.label} style={{ background: '#fff', borderRadius: '14px', padding: '16px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{item.label}</strong>
                  <span style={{ fontSize: '14px', color: '#111', lineHeight: '1.5' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 도구 목록 */}
        <section style={{ maxWidth: '960px', margin: '0 auto', padding: '40px 20px 80px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {tools.map((tool, idx) => (
              <div key={idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '28px 24px', position: 'relative', cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onClick={() => navigate(tool.link)}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#3182f6'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(49,130,246,0.1)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {tool.badge && (
                  <span style={{ position: 'absolute', top: '12px', right: '12px', padding: '3px 10px', fontSize: '11px', fontWeight: '700', borderRadius: '12px', background: tool.badge === 'NEW' ? '#dcfce7' : '#dbeafe', color: tool.badge === 'NEW' ? '#15803d' : '#1d4ed8' }}>
                    {tool.badge}
                  </span>
                )}
                <div style={{ display: 'flex', marginBottom: '12px' }}><NamedGrayIcon name={tool.icon} /></div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{tool.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', marginBottom: tool.guide ? '12px' : '0' }}>{tool.desc}</p>
                {tool.guide && (
                  <span onClick={(e) => { e.stopPropagation(); navigate(tool.guide); }} style={{ fontSize: '13px', color: '#3182f6', fontWeight: '600', cursor: 'pointer' }}>
                    상세 가이드 보기 →
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 왜 왓퀴즈인가 */}
        <section style={{ background: '#f8fafc', padding: '60px 20px' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>왜 왓퀴즈일까요?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {[
                { title: '올인원 플랫폼', desc: '생기부, PPT, 학습지, 채점, 이미지까지 한 곳에서. 여러 도구 사이 이동 없이 교사 업무 전반을 처리합니다.' },
                { title: `${TEACHER_COUNT_LABEL} 교사 검증`, desc: 'KERIS 에듀테크 우수기업. 실제 교사들이 매일 사용하며 검증한 도구입니다.' },
                { title: '학교 예산 결제', desc: '에듀집 등록. 법인카드 결제, 세금계산서 발급 지원. 학운위 심의 대상이 아니라 품의만으로 도입할 수 있습니다.' },
                { title: '합리적 가격', desc: `무료로 시작 가능. 시즌 특가 기준 PRO는 6개월 ${_aiToolsPrices['6month'].toLocaleString()}원, 12개월 ${_aiToolsPrices['12month'].toLocaleString()}원입니다.` },
              ].map((item, idx) => (
                <div key={idx} style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '6px' }}>{item.title}</h3>
                  <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #f0f7ff 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>교사를 위해 만들어진 AI,<br />직접 경험해보세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>교사 인증 후 무료로 시작할 수 있습니다</p>
          <button onClick={() => navigate('/chat')} style={{ padding: '14px 28px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
            무료로 시작하기
          </button>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AiTeacherToolsGuidePage;
